import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Tabs,
  SegmentedControl,
  TextInput,
  Textarea,
  Button,
  Stack,
  Box,
  SimpleGrid,
  ScrollArea,
  Input,
  Image,
  MantineProvider,
  useMantineTheme,
  Select,
  Center,
  Loader,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import * as FilePond from "filepond";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
import Swal from "sweetalert2";

import PremiumModal from "../../app/PremiumModal";

import MediaFileModel from "../../../models/MediaFile";
import SpaceModel from "../../../models/Space";

import { getEnv } from "../../../utils/Host";
import { sutraTheme } from "../../../collections/sutraTheme";
import SERVICES from "../collections/embedRegex";
import { createNode, getEmbedLinkType, createMediaFile, handleFileUpload } from "../utils/fileHandler";

FilePond.registerPlugin(FilePondPluginImageResize, FilePondPluginImageTransform);

const UNSPLASH_ACCESS_KEY = "4BHv6-IGLP-oa0rhTeyUnT40xuClCYynK9e0ZwWjpbA";
let unsplashSearchTimeout = null;

export default function Uploader(props) {
  const space = useSelector((state) => state.space);
  const dispatch = useDispatch();
  const theme = useMantineTheme();
  const { t } = useTranslation();
  
  const [activeTab, setActiveTab] = useState(0);
  const [uploadType, setUploadType] = useState(props.uploadType || "image");
  const [embedType, setEmbedType] = useState("link");
  const [embedLink, setEmbedLink] = useState("");
  const [embedCode, setEmbedCode] = useState("");
  const [zoomData, setZoomData] = useState({ loading: true });
  const [zoomUserId, setZoomUserId] = useState("");
  const [zoomMeetingId, setZoomMeetingId] = useState("");
  const [unsplashImages, setUnsplashImages] = useState([]);
  const [canUseZoom, setCanUseZoom] = useState(false);
  const [opened, setOpened] = useState([]);
  const [selectedUnsplashImage, setSelectedUnsplashImage] = useState(null);

  const filePond = useRef();
  const imageInput = useRef();
  const lastZoomUser = useRef();

  useEffect(() => {
    return () => {
      filePond.current = null;
    };
  }, []);

  useEffect(() => {
    setCanUseZoom(space?.permissions?.can_use_zoom_recordings);
  }, [space]);

  useEffect(() => {
    if (props.uploadType) {
      setUploadType(props.uploadType);
      
      if (props.uploadType === "video") {
        setActiveTab(1);
      } else if (props.uploadType === "embed") {
        setActiveTab(0);
      } else if (props.uploadType !== "image") {
        setActiveTab(1);
        setTimeout(() => setActiveTab(0), 100);
      }
    }

    if (unsplashImages.length === 0) {
      fetchUnsplashPhotos();
    }
  }, [props]);

  useEffect(() => {
    if (uploadType === "image" && !filePond.current) {
      initFilePond();
    }
    
    if (uploadType === "zoom" && !zoomData.connected) {
      fetchZoomData();
    }
  }, [uploadType]);

  useEffect(() => {
    if (uploadType === "zoom" && lastZoomUser.current !== zoomUserId) {
      fetchZoomData();
    }
  }, [zoomUserId]);

  const getAcceptedTypes = () => {
    const typeMap = {
      "image": "image/*",
      "video": ".mp4,.avi,.mkv,.webm,.mov,.yuv",
      "audio": "audio/*",
      "file": "pdf,doc,docx,xls,xlsx,ppt,pptx"
    };
    
    return typeMap[uploadType] || null;
  };

  const fetchUnsplashPhotos = (searchTerm) => {
    const url = searchTerm ? "/search/photos" : "/photos";
    const params = {
      client_id: UNSPLASH_ACCESS_KEY,
      query: searchTerm,
      page: 1,
      per_page: 30
    };

    axios.get(`https://api.unsplash.com/${url}`, { params })
      .then((res) => setUnsplashImages(searchTerm ? res.data.results : res.data))
      .catch((error) => console.error("Unsplash fetch error:", error));
  };

  const handleUnsplashSearch = (event) => {
    if (unsplashSearchTimeout) {
      clearTimeout(unsplashSearchTimeout);
    }
    
    unsplashSearchTimeout = setTimeout(() => {
      unsplashSearchTimeout = null;
      fetchUnsplashPhotos(event.target.value);
    }, 2000);
  };

  const saveUnsplashImage = () => {
    if (props.uploadToEndpoint) {
      props.uploadToEndpoint(selectedUnsplashImage?.urls?.regular);
      return;
    }
    createNode({ type: "unsplash", url: selectedUnsplashImage, editor: props.editor, dispatch, onSave: props.onSave });
  };

  const getEmbedUrl = (code) => {
    for (const [serviceName, serviceConfig] of Object.entries(SERVICES)) {
      if (serviceConfig.regex.test(code)) {
        const { regex, embedUrl, splice, id = (ids) => ids.shift() } = serviceConfig;
        
        if (splice === "iframe") {
          const result = regex.exec(code);
          return [result[3], result[3]];
        } else {
          const result = regex.exec(code).slice(1);
          let embed = embedUrl.replace(/<%= remote_id %>/g, id(result));
          
          if (splice === 2) {
            const result2 = regex.exec(code).slice(3);
            embed = embed.replace(/<%= remote_id2 %>/g, id(result2));
          }
          
          return [embed, id(result)];
        }
      }
    }
    
    return null;
  };

  const saveEmbed = () => {
    if (embedType === "link") {
      const url = getEmbedUrl(embedLink);
      
      if (url && url.length > 0) {
        if (props.action === "update") {
          props.editor.commands.updateAttributes("embed", {
            src: url[0],
            videoId: url[1]
          });
        } else {
          props.editor.chain().setEmbed({
            videoId: url[1],
            src: url[0],
            textAlign: !props.notMain ? "center" : "left",
            resizeableWidth: "100%",
            radius: "with-radius"
          }).run();
        }
        
        props.onSave();
        createEmbedMediaFile(url[0], url[1]);
      } else {
        createNode({ type: getEmbedLinkType(embedLink), url: embedLink, editor: props.editor, dispatch, onSave: props.onSave });
      }
    } else {
      const url = getEmbedUrl(embedCode);
      
      if (url && url.length > 0) {
        if (props.action === "update") {
          props.editor.commands.updateAttributes("embed", { src: url[0] });
        } else {
          props.editor.chain().setEmbed({
            src: url[0],
            videoId: url[1],
            radius: "with-radius",
            resizeableWidth: "100%"
          }).run();
        }
        
        props.onSave();
        createEmbedMediaFile(url[0], url[1]);
      }
    }
  };

  const createEmbedMediaFile = (originalUrl, embedId) => {
    const mediaFile = {
      original_url: originalUrl,
      url: originalUrl,
      upload_type: "embed",
      slug: window.$currentSlug,
      embed_id: embedId
    };

    MediaFileModel.onCreate(
      mediaFile,
      () => console.log("Embed media file stored"),
      () => {}
    );
  };

  const handleFileSelect = (event) => {
    event.target.children[0]?.click();
  };

  const handleVideoFileInput = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (props.uploadToEndpoint) {
      props.uploadToEndpoint(file);
      return;
    }

    handleFileUpload({ file, editor: props.editor, dispatch, uploadType, space, notMain: props.notMain, onSave: props.onSave });
  };

  const initFilePond = () => {
    const inputElement = imageInput.current;
    if (filePond.current || !inputElement) return;

    filePond.current = FilePond.create(inputElement, {
      imageResizeTargetWidth: 1080,
      imageResizeUpscale: false,
      imageTransformOutputQuality: 70,
      beforeAddFile: (file) => {
        if (file.file.size > 5 * 1024 * 1024) {
          Swal.fire({
            icon: "info",
            title: "File is Too Big",
            text: "The file you selected is larger than the 5MB limit. Please choose a smaller file.",
            confirmButtonText: "Ok"
          }).then((result) => {});
          return false;
        }
        return true;
      },
      server: {
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          if (file.type.indexOf("image") < 0) return;
          
          if (props.uploadToEndpoint) {
            props.uploadToEndpoint(file);
            return;
          }

          handleFileUpload({ file, editor: props.editor, dispatch, uploadType, space, notMain: props.notMain, onSave: props.onSave });
        },
      },
    });
  };

  const fetchZoomData = () => {
    SpaceModel.onZoomData(
      space.slug,
      zoomUserId,
      (data) => {
        setZoomData({ ...data.zoom_data });
        if (data.zoom_data.users?.length === 1) {
          lastZoomUser.current = data.zoom_data.users[0].value;
          setZoomUserId(data.zoom_data.users[0].value);
        }
      },
      () => {}
    );
  };

  const handleConnectZoom = () => {
    if (!canUseZoom) {
      setOpened([true, canUseZoom?.[1], canUseZoom?.[2]]);
      return;
    }

    const baseUrl = "https://zoom.us/oauth/authorize";
    const clientId = getEnv() === "production" 
      ? "Iq_GKcCRhWRVRdVaE3W5Q" 
      : "YK0Cx_LQ16AVBy0FFvaxQ";
    const redirectUri = getEnv() === "production"
      ? "https://sutra.co/api/v4/hooks/zoom/redirect"
      : "https://sutra.ngrok.dev/api/v4/hooks/zoom/redirect";
    
    window.location.href = `${baseUrl}?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}?state=${space.slug}_usr_${window.$currentUserAuthToken}`;
  };

  const createZoomRecording = () => {
    if (!canUseZoom) {
      setOpened([true, canUseZoom?.[1], canUseZoom?.[2]]);
      return;
    }

    const uid = uuidv4().replace(/-/g, "");
    const videoId = `zoom_recording_${zoomMeetingId}_${uid}`;
    const url = `https://sutra-video-output.s3.us-west-1.amazonaws.com/${videoId}.m3u8`;

    handleFileUpload({
      file: null,
      editor: props.editor,
      dispatch,
      uploadType: "video",
      space,
      notMain: props.notMain,
      onSave: props.onSave,
      extras: {
        id: uid,
        src: url,
        uploader: {
          id: `zoom/${videoId}.mp4`,
          status: "zoom-pending",
          progress: 0,
          url: url,
        },
        textAlign: !props.notMain ? "center" : "left",
        resizeableWidth: "100%",
        radius: "with-radius",
      }
    });

    createMediaFile(`zoom/${videoId}.mp4`, url, "zoom-recording");
    props.onSave();
  };

  return (
    <MantineProvider theme={sutraTheme}>
      <Box>
        <Tabs
          styles={{
            tabsList: {
              flexWrap: "nowrap",
            },
            tabActive: {
              fontWeight: "bold",
              color: `${theme.colors.dark[9]} !important`,
              borderBottomColor: `${sutraTheme.colors.sutrablue[4]} !important`,
            },
            tabLabel: { color: theme.colors.gray[7] },
          }}
          active={activeTab}
          onTabChange={setActiveTab}
        >
          {uploadType !== "embed" && (
            <Tabs.Tab label={uploadType === "zoom" ? "Zoom recording" : "Upload"}>
              <Box>
                {uploadType === "image" ? (
                  <Button radius="xl" fullWidth onClick={handleFileSelect}>
                    <input
                      ref={imageInput}
                      type="file"
                      style={{ display: "none" }}
                      accept={getAcceptedTypes()}
                    />
                    {`Select ${uploadType}`}
                  </Button>
                ) : uploadType === "zoom" ? (
                  <>
                    {zoomData.connected ? (
                      <>
                        {zoomData.users && zoomData.users.length > 1 && (
                          <Select
                            zIndex={100000}
                            label="Select a user"
                            placeholder="Pick one"
                            data={zoomData.users}
                            value={zoomUserId}
                            onChange={setZoomUserId}
                            searchable
                            mb={20}
                          />
                        )}
                        <Select
                          zIndex={100000}
                          label="Select meeting"
                          placeholder="Pick one"
                          data={zoomData.meetings}
                          value={zoomMeetingId}
                          onChange={setZoomMeetingId}
                          searchable
                        />
                        <Button
                          onClick={createZoomRecording}
                          disabled={!zoomMeetingId}
                          mt={20}
                          radius="xl"
                        >
                          Create recording placeholder
                        </Button>
                      </>
                    ) : zoomData.loading ? (
                      <Center>
                        <Loader />
                      </Center>
                    ) : (
                      <Button onClick={handleConnectZoom} radius="xl">
                        Connect Zoom Account
                      </Button>
                    )}
                  </>
                ) : (
                  <Button radius="xl" fullWidth onClick={handleFileSelect}>
                    <input
                      type="file"
                      style={{ display: "none" }}
                      accept={getAcceptedTypes()}
                      onChange={handleVideoFileInput}
                    />
                    {`Select ${uploadType}`}
                  </Button>
                )}
              </Box>
            </Tabs.Tab>
          )}
          
          {(!props.exclude || 
            (props.exclude && props.exclude.indexOf("embed") < 0 && uploadType !== "zoom")) && (
            <Tabs.Tab label="Embed">
              <Box>
                <Stack>
                  <SegmentedControl
                    value={embedType}
                    onChange={setEmbedType}
                    data={[
                      { label: t("tiptap.components.uploader.label1"), value: "link" },
                      { label: t("tiptap.components.uploader.label2"), value: "code" }
                    ]}
                  />
                  
                  {embedType === "link" ? (
                    <TextInput
                      value={embedLink}
                      onChange={(event) => setEmbedLink(event.target.value)}
                      placeholder={t("tiptap.components.uploader.placeholder")}
                      label={t("tiptap.components.uploader.label3")}
                    />
                  ) : (
                    <Textarea
                      value={embedCode}
                      onChange={(event) => setEmbedCode(event.target.value)}
                      placeholder={t("tiptap.components.uploader.placeholder2")}
                      label={t("tiptap.components.uploader.label4")}
                    />
                  )}
                  
                  <Button radius="xl" fullWidth onClick={saveEmbed}>
                    {t("tiptap.components.uploader.saveEmbed")} {embedType}
                  </Button>
                </Stack>
              </Box>
            </Tabs.Tab>
          )}
          
          {uploadType === "image" && (
            <Tabs.Tab label="Unsplash">
              <Box>
                <Stack style={{ height: "100%" }}>
                  <Input
                    onChange={handleUnsplashSearch}
                    icon={<FontAwesomeIcon icon={solid("search")} />}
                    placeholder={t("tiptap.components.uploader.placeholder3")}
                  />
                  
                  <ScrollArea style={{ height: "100%" }}>
                    <SimpleGrid cols={5} style={{ height: props.unsplashHeight || 140 }}>
                      {unsplashImages.map((image) => (
                        <div
                          className={selectedUnsplashImage?.urls?.regular === image.urls.regular
                            ? "unsplash-selected"
                            : "unsplash-img"}
                          onClick={() => setSelectedUnsplashImage(image)}
                          key={image.id}
                        >
                          <Image
                            radius="md"
                            src={image.urls.small}
                            alt={image.description || "Unsplash image"}
                          />
                        </div>
                      ))}
                    </SimpleGrid>
                  </ScrollArea>
                  
                  <Button
                    radius="xl"
                    fullWidth
                    disabled={!selectedUnsplashImage}
                    onClick={saveUnsplashImage}
                  >
                    {t("tiptap.components.uploader.saveImage")}
                  </Button>
                </Stack>
              </Box>
            </Tabs.Tab>
          )}
        </Tabs>
      </Box>
      <PremiumModal opened={opened} setOpened={setOpened} />
    </MantineProvider>
  );
}